import { create } from 'zustand';
import { documentsAPI, pagesAPI } from '../services/api';
import toast from 'react-hot-toast';

// Store polling intervals outside of Zustand state (not serializable)
const pollingIntervals = {};

// DEBUG: Make polling intervals accessible from console
if (typeof window !== 'undefined') {
  window.__DEBUG_POLLING_INTERVALS__ = pollingIntervals;
}

const useDocumentStore = create((set, get) => ({
  documents: [],
  currentDocument: null,
  currentPage: null,
  isLoading: false,
  isFetching: false, // Lock to prevent concurrent fetches
  error: null,
  pagination: { page: 1, totalPages: 1, total: 0 },

  // Global tracking of documents currently being processed
  // This persists across component navigation
  processingDocuments: {}, // { documentId: { progress: number, startedAt: timestamp } }

  // Track recently completed documents to prevent API responses from overwriting optimistic updates
  // This fixes the race condition where component polling overwrites is_processed: true
  recentlyCompletedDocuments: new Set(),

  fetchDocuments: async (params = {}, options = {}) => {
    const { silent = false, signal } = options;

    // Skip if already fetching (prevents race conditions from multiple polling sources)
    if (get().isFetching && silent) {
      return;
    }

    if (!silent) {
      set({ isLoading: true, error: null });
    }
    set({ isFetching: true });

    try {
      // Default to 15 items per page
      const queryParams = { per_page: 15, ...params };
      const response = await documentsAPI.list(queryParams, signal);

      // Preserve is_processed: true for recently completed documents
      // This prevents the race condition where API response (with stale is_processed: false)
      // overwrites the optimistic update we made when processing completed
      const recentlyCompleted = get().recentlyCompletedDocuments;
      const rawDocs = response.data.results || response.data;
      const mergedDocs = rawDocs.map(doc => {
        if (recentlyCompleted.has(doc.id)) {
          console.log(`[Store] Preserving is_processed=true for recently completed doc ${doc.id}`);
          return { ...doc, is_processed: true, has_error: false };
        }
        return doc;
      });

      set({
        documents: mergedDocs,
        pagination: {
          page: response.data.page || 1,
          totalPages: response.data.total_pages || 1,
          total: response.data.total || response.data.length,
        },
        isLoading: false,
        isFetching: false,
      });
    } catch (error) {
      // Ignore cancelled requests (AbortController)
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        set({ isFetching: false });
        return;
      }
      set({ isFetching: false });
      if (!silent) {
        set({ error: error.message, isLoading: false });
      }
    }
  },

  fetchDocument: async (id, options = {}) => {
    const { silent = false } = options;
    // Only set isLoading if not a silent/polling request
    if (!silent) {
      set({ isLoading: true, error: null });
    }
    try {
      const response = await documentsAPI.get(id);
      set({ currentDocument: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      if (!silent) {
        set({ error: error.message, isLoading: false });
      }
      return null;
    }
  },

  uploadDocument: async (formData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await documentsAPI.upload(formData);
      set({ isLoading: false });
      return { success: true, data: response.data };
    } catch (error) {
      const backendError = error.response?.data?.error || error.message;
      set({ error: backendError, isLoading: false });

      // Handle duplicate document error specifically
      if (error.response?.status === 409) {
        return {
          success: false,
          error: backendError,
          isDuplicate: true,
          existingDocumentId: error.response.data.existing_document_id
        };
      }

      // Malware detected
      if (error.response?.status === 422 && error.response?.data?.is_malware) {
        return { success: false, error: backendError, isMalware: true };
      }

      // Virus scanner unavailable
      if (error.response?.status === 503) {
        return { success: false, error: backendError, isScannerDown: true };
      }

      return { success: false, error: backendError };
    }
  },

  deleteDocument: async (id) => {
    try {
      await documentsAPI.delete(id);
      set((state) => ({
        documents: state.documents.filter((doc) => doc.id !== id),
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  fetchPage: async (id, options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      set({ isLoading: true });
    }
    try {
      const response = await pagesAPI.get(id);
      set({ currentPage: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      if (!silent) {
        set({ error: error.message, isLoading: false });
      }
      return null;
    }
  },

  saveCrop: async (pageId, cropData) => {
    try {
      const response = await pagesAPI.saveCrop(pageId, cropData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  saveTranscription: async (pageId, data) => {
    try {
      const response = await pagesAPI.saveTranscription(pageId, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  clearError: () => set({ error: null }),
  clearCurrentDocument: () => set({ currentDocument: null, currentPage: null }),

  // Processing state management - persists across navigation
  // Polling happens in the STORE, not in components, so it continues when navigating

  startProcessingWithPolling: async (documentId, documentTitle) => {
    console.log(`[Store] Starting processing for document ${documentId}`);
    console.log(`[Store] Current processingDocuments:`, get().processingDocuments);
    console.log(`[Store] Current pollingIntervals:`, Object.keys(pollingIntervals));

    // Set initial processing state
    set((state) => ({
      processingDocuments: {
        ...state.processingDocuments,
        [documentId]: { progress: 5, startedAt: Date.now(), title: documentTitle }
      }
    }));

    console.log(`[Store] After set, processingDocuments:`, get().processingDocuments);

    // Clear any existing polling for this document
    if (pollingIntervals[documentId]) {
      clearInterval(pollingIntervals[documentId]);
    }

    // Start polling in the store (this continues even when component unmounts)
    const pollStatus = async () => {
      console.log(`[Store] Polling doc ${documentId}, current processingDocuments:`, get().processingDocuments);
      try {
        const statusResponse = await documentsAPI.getStatus(documentId);
        const status = statusResponse.data;
        console.log(`[Store] Poll for doc ${documentId}: progress=${status.progress}, status=${status.status}, full response:`, status);

        // Update progress in store
        set((state) => ({
          processingDocuments: {
            ...state.processingDocuments,
            [documentId]: {
              ...state.processingDocuments[documentId],
              progress: status.progress || 0
            }
          }
        }));

        // Check if completed or failed
        if (status.status === 'completed') {
          console.log(`[Store] Document ${documentId} processing completed! Will clear from processingDocuments`);
          console.log(`[Store] Before clear, processingDocuments:`, get().processingDocuments);
          clearInterval(pollingIntervals[documentId]);
          delete pollingIntervals[documentId];

          // Remove from processing documents, add to recently completed, and optimistically update
          // The recentlyCompletedDocuments set prevents component polling from overwriting is_processed
          set((state) => {
            const { [documentId]: removed, ...rest } = state.processingDocuments;
            const newRecentlyCompleted = new Set(state.recentlyCompletedDocuments);
            newRecentlyCompleted.add(documentId);
            console.log(`[Store] Added doc ${documentId} to recentlyCompletedDocuments`);
            return {
              processingDocuments: rest,
              recentlyCompletedDocuments: newRecentlyCompleted,
              // Optimistically mark document as processed in the documents list
              documents: state.documents.map(doc =>
                doc.id === documentId ? { ...doc, is_processed: true, has_error: false } : doc
              )
            };
          });

          // Remove from recently completed after 10 seconds (backend should have persisted by then)
          setTimeout(() => {
            set((state) => {
              const newSet = new Set(state.recentlyCompletedDocuments);
              newSet.delete(documentId);
              console.log(`[Store] Removed doc ${documentId} from recentlyCompletedDocuments after 10s`);
              return { recentlyCompletedDocuments: newSet };
            });
          }, 10000);

          // Show success toast
          toast.success(`Document processed successfully!`, {
            duration: 4000,
            icon: '✅'
          });

        } else if (status.status === 'failed' || status.has_error) {
          console.log(`[Store] Document ${documentId} processing failed! status=${status.status}, has_error=${status.has_error}`);
          console.log(`[Store] Before clear, processingDocuments:`, get().processingDocuments);
          clearInterval(pollingIntervals[documentId]);
          delete pollingIntervals[documentId];

          // Remove from processing documents
          set((state) => {
            const { [documentId]: removed, ...rest } = state.processingDocuments;
            return { processingDocuments: rest };
          });

          // Force immediate fetch of document to update UI state and error displays
          console.log(`[Store] Triggering post-failure fetch for ${documentId}`);
          get().fetchDocument(documentId, { silent: true });

          // Show error toast
          toast.error(status.error_message || 'Processing failed', {
            duration: 8000,
            icon: '❌'
          });
        }
      } catch (error) {
        console.error(`[Store] Error polling document ${documentId}:`, error);
        console.log(`[Store] processingDocuments after error:`, get().processingDocuments);
        // Continue polling on network errors
      }
    };

    // Start polling immediately, then every 2 seconds
    pollStatus();
    pollingIntervals[documentId] = setInterval(pollStatus, 2000);
  },

  updateProcessingProgress: (documentId, progress) => {
    set((state) => ({
      processingDocuments: {
        ...state.processingDocuments,
        [documentId]: {
          ...state.processingDocuments[documentId],
          progress
        }
      }
    }));
  },

  stopProcessing: (documentId) => {
    console.log(`[Store] Stopping processing for document ${documentId}`);
    // Clear polling interval
    if (pollingIntervals[documentId]) {
      clearInterval(pollingIntervals[documentId]);
      delete pollingIntervals[documentId];
    }
    // Remove from state
    set((state) => {
      const { [documentId]: removed, ...rest } = state.processingDocuments;
      return { processingDocuments: rest };
    });
  },

  isDocumentProcessing: (documentId) => {
    const state = get();
    return !!state.processingDocuments[documentId];
  },

  getProcessingProgress: (documentId) => {
    const state = get();
    return state.processingDocuments[documentId]?.progress || 0;
  },

  // Legacy function for backwards compatibility
  startProcessing: (documentId) => {
    get().startProcessingWithPolling(documentId, '');
  },
}));

// DEBUG: Make store accessible from console
if (typeof window !== 'undefined') {
  window.__DEBUG_DOCUMENT_STORE__ = useDocumentStore;
}

export default useDocumentStore;
